import { BottomCenteredDrawer } from "@jugl-web/ui-components/web/BottomCenteredDrawer";
import React, { useState } from "react";
import { getUniqueId, useTranslations } from "@jugl-web/utils";
import { MultiSectionLayout } from "@jugl-web/ui-components";
import { ChatBotChannelPreview } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatBotChannelPreview";
import { ChatbotPreviewBackground } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/screens/WebChatBotAccountScreen/components/ChatbotPreviewBackground";
import { useManageAccountsDrawer } from "@web-src/modules/client-support/pages/ClientSupportPage/components/ManageAccountsDrawer/providers/ManageAccountsDrawerContext";
import { resolveChatBotSettings } from "@jugl-web/domain-resources/chat-bot/utils/resolveChatBotSettings";
import { AgentInstructionsPanel } from "./components/AgentInstructionsPanel";
import { EscalateToHumanPanel } from "./components/EscalateToHumanPanel";
import { WhenToCollectLeadPanel } from "./components/WhenToCollectLeadPanel";
import { AgentStylePanel } from "./components/AgentStylePanel";
import { ReactComponent as AIAgentIcon } from "./assets/ai-agent.svg";

export const SetupAndTestTabMenu: React.FC<{
  entityId: string;
  onAddAutomation: () => void;
}> = ({ entityId, onAddAutomation }) => {
  const { t } = useTranslations();
  const [isInstructionsDrawerOpen, setIsInstructionsDrawerOpen] =
    useState(false);
  const { crmConfig } = useManageAccountsDrawer();
  const chatBotSettings = resolveChatBotSettings(crmConfig?.chatbot?.settings);

  return (
    <>
      <div className="jugl__custom-scrollbar h-full w-[464px] overflow-y-auto border-0 border-r border-solid border-dark-200 pt-8 pb-[100px]">
        <AgentInstructionsPanel
          entityId={entityId}
          onRequestFullscreen={() => setIsInstructionsDrawerOpen(true)}
          onAddAutomation={onAddAutomation}
        />

        <div className="my-8 h-px w-full shrink-0 bg-[#F2F2F2]" />

        <WhenToCollectLeadPanel entityId={entityId} />

        <div className="my-8 h-px w-full shrink-0 bg-[#F2F2F2]" />

        <EscalateToHumanPanel entityId={entityId} />

        <div className="my-8 h-px w-full shrink-0 bg-[#F2F2F2]" />

        <div className="mx-8 flex flex-col gap-8">
          <span className="font-secondary font-medium text-black">
            {t({
              id: "cx-ai-agents-page.agent-style-section",
              defaultMessage: "🤖 Agent Style",
            })}
          </span>
          <AgentStylePanel entityId={entityId} />
        </div>
      </div>

      <BottomCenteredDrawer
        isOpen={isInstructionsDrawerOpen}
        onClose={() => setIsInstructionsDrawerOpen(false)}
        size="fullscreen"
        header={{
          title: (
            <div className="flex items-center gap-3">
              <AIAgentIcon />
              <span>
                {t({
                  id: "cx-ai-agents-page.instructions",
                  defaultMessage: "Instructions",
                })}
              </span>
            </div>
          ),
          type: "title",
        }}
      >
        <div className="flex min-h-0 flex-1 overflow-hidden">
          <MultiSectionLayout
            className="h-full w-full"
            rightSide={
              <div className="h-full w-[570px] border-0 border-l border-solid border-dark-100">
                <ChatbotPreviewBackground>
                  <ChatBotChannelPreview
                    entityId={entityId}
                    chatName={chatBotSettings.chatName}
                    agentName={chatBotSettings.agentName}
                    welcomeMessage={chatBotSettings.welcomeMessage}
                    senderMessageBackgroundColor={
                      chatBotSettings.senderMessageBackgroundColor
                    }
                    theme={chatBotSettings.theme}
                    buttonColor={chatBotSettings.buttonColor}
                    alignment={chatBotSettings.alignment}
                    bottomSpacing={chatBotSettings.bottomSpacing}
                    sideSpacing={chatBotSettings.sideSpacing}
                    chatImage={chatBotSettings.chatImageUrl}
                    agentImage={chatBotSettings.agentImage}
                    buttonIcon={chatBotSettings.buttonIcon}
                    ctaVisual={chatBotSettings.ctaVisual?.url ?? null}
                    ctas={chatBotSettings.ctas.map((item) => ({
                      id: getUniqueId(),
                      value: item,
                    }))}
                    suggestions={chatBotSettings.suggestions.map((item) => ({
                      id: getUniqueId(),
                      value: item,
                    }))}
                    chatMode="active"
                    previewMode="chat-only"
                  />
                </ChatbotPreviewBackground>
              </div>
            }
          >
            <AgentInstructionsPanel
              variant="drawer"
              entityId={entityId}
              onAddAutomation={onAddAutomation}
            />
          </MultiSectionLayout>
        </div>
      </BottomCenteredDrawer>
    </>
  );
};
